/**
 * The session query, shared app-wide under one cache key. Screens read the
 * server-declared permission set from here (docs/03 §4) — the client renders
 * controls from what the server says, never guesses from the role name.
 */

import { useQuery } from '@tanstack/react-query';

import { api, ApiError, type Me } from './api.js';

export function useMe(): { me: Me | null | undefined; error: unknown } {
  const query = useQuery({
    queryKey: ['me'],
    queryFn: async (): Promise<Me | null> => {
      try {
        return await api<Me>('/api/v1/auth/me');
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    staleTime: 60_000,
    retry: 1,
  });
  return { me: query.data, error: query.error };
}

/** True when the signed-in user's materialized permissions include the key. */
export function usePermission(key: string): boolean {
  const { me } = useMe();
  return me?.permissions.includes(key) ?? false;
}
